import { useQuery } from '@tanstack/react-query';
import { getProductAmountHistory } from '@/services/firebaseApi';
import { ProductAmountHistoryEntry } from '@/types';
import { useMemo } from 'react';

export enum HistoryTimeRange {
    WEEK = 7,
    MONTH = 30,
    QUARTER = 90,
    YEAR = 365,
}

interface UseProductHistoryProps {
    storageId: string;
    productId: string;
    timeRange: HistoryTimeRange;
}

/**
 * Aggregates history entries by week for data older than 90 days
 */
const aggregateByWeek = (entries: ProductAmountHistoryEntry[], cutoffDate: Date) => {
    const weeklyData: Record<string, ProductAmountHistoryEntry & { count: number }> = {};

    entries.forEach((entry) => {
        const entryDate = entry.keyHistory?.createdAt?.toDate();
        if (!entryDate) return;
        
        // Only aggregate entries older than 90 days
        if (entryDate >= cutoffDate) {
            return;
        }

        // Get the week start (Monday)
        const weekStart = new Date(entryDate);
        const day = weekStart.getDay();
        const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
        weekStart.setDate(diff);
        weekStart.setHours(0, 0, 0, 0);
        
        const weekKey = weekStart.toISOString();

        if (!weeklyData[weekKey]) {
            weeklyData[weekKey] = {
                keyHistory: {
                    createdAt: entry.keyHistory?.createdAt,
                },
                amount: entry.amount,
                delta: 0,
                actionType: entry.actionType,
                ids: entry.ids,
                count: 0,
            };
        }

        weeklyData[weekKey].delta += entry.delta;
        weeklyData[weekKey].count += 1;
        // Use the most recent amount for the week
        const currentEntryDate = entry.keyHistory?.createdAt?.toDate();
        const weekDataDate = weeklyData[weekKey].keyHistory?.createdAt?.toDate();
        if (currentEntryDate && weekDataDate && currentEntryDate > weekDataDate) {
            weeklyData[weekKey].amount = entry.amount;
            weeklyData[weekKey].keyHistory = { createdAt: entry.keyHistory?.createdAt };
        }
    });

    return Object.values(weeklyData).map(({ count, ...entry }) => entry);
};

const useProductHistory = ({ storageId, productId, timeRange }: UseProductHistoryProps) => {
    // Fetch history data
    const {
        data: rawHistory,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['productHistory', storageId, productId, timeRange],
        queryFn: () => getProductAmountHistory({ 
            storageId, 
            productId, 
            daysBack: timeRange 
        }),
        enabled: !!(storageId && productId),
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
    });

    // Process and aggregate history data
    const history = useMemo(() => {
        if (!rawHistory || rawHistory.length === 0) return [];

        // Cast and validate the data
        const typedHistory = rawHistory as unknown as ProductAmountHistoryEntry[];

        // Sort by date ascending for chart display
        const sortedHistory = [...typedHistory].sort((a, b) => {
            const aTime = a.keyHistory?.createdAt?.toMillis() || 0;
            const bTime = b.keyHistory?.createdAt?.toMillis() || 0;
            return aTime - bTime;
        });

        // If time range is year, aggregate older data by week
        if (timeRange === HistoryTimeRange.YEAR) {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - 90);
            
            // Separate recent (last 90 days) and older entries
            const recentEntries = sortedHistory.filter(
                entry => {
                    const entryDate = entry.keyHistory?.createdAt?.toDate() || new Date(0);
                    return entryDate >= cutoffDate;
                }
            );
            
            const olderEntries = sortedHistory.filter(
                entry => {
                    const entryDate = entry.keyHistory?.createdAt?.toDate() || new Date(0);
                    return entryDate < cutoffDate;
                }
            );

            // Aggregate older entries by week
            const aggregatedOlder = aggregateByWeek(olderEntries, cutoffDate);
            
            // Combine and sort
            return [...aggregatedOlder, ...recentEntries].sort((a, b) => {
                const aTime = a.keyHistory?.createdAt?.toMillis() || 0;
                const bTime = b.keyHistory?.createdAt?.toMillis() || 0;
                return aTime - bTime;
            });
        }

        return sortedHistory;
    }, [rawHistory, timeRange]);

    return {
        history: history,
        isLoading,
        error,
        refetch,
    };
};

export default useProductHistory;
